import { useEffect, CSSProperties, memo, Fragment, useMemo } from 'react';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import styles from './popover.module.css';
import Button from '@ui/buttons/button/button';
import Portal from '@ui/portal/portal';
import { mergeRefs } from '@/utils/merge-refs';
import useCalculatePosition from './helpers/use-calculate-position';
import { PopoverProps } from './popover.types';
import { usePopoverState } from './helpers/use-popover-state';
import { usePopoverTimeouts } from './helpers/use-popover-timeouts';
import { useOpenAutoFocus } from './helpers/use-open-auto-focus';
import { useRefocusTriggerOnClose } from './helpers/use-refocus-trigger-on-close';
import { usePopoverInteractionHandlers } from './helpers/use-popover-interaction-handlers';
import { useTriggerDimensionTracking } from './helpers/use-trigger-dimension-tracking';
import { useTriggerButtonClone } from './helpers/use-trigger-button-clone';
import { usePopoverClickAway } from './helpers/use-popover-click-away';
import { usePopoverKeyboardControls } from './helpers/use-popover-keyboard-controls';
import { useControlledPopover } from './helpers/use-controlled-popover';
import { buildAnimationStyles } from './helpers/build-animation-styles';
import { buildContentContainerStyles } from './helpers/build-content-container-styles';
import * as EventEmitter from '@/utils/event-emitter';

const Popover = memo(
  ({
    ref,
    id: propsId,
    popoverContentRef,
    recalcutePosition,
    onOpen,
    onClose,
    defaultOpen = false,
    height,
    maxHeight,
    closeOnTabAway: _closeOnTabAway,
    closeOnEscape = true,
    width = '200px',
    renderTriggerButton,
    children,
    forcePosition,
    preferredPosition,
    closeOnClick = false,
    closeOnEnter = false,
    backgroundColor,
    forceTransformIn,
    forceTransformOut,
    containerClassName,
    contentClassName,
    popoverWidth,
    icon,
    buttonBgColor,
    disabled,
    overflow = 'auto',
    radius,
    inheritRadius,
    horizontalOffset = 0,
    verticalOffset = 0,
    keepOpenOnTriggerToggle,
    open: controlledOpen,
    iconColor,
    iconColorLightMode,
    cycleWithArrowKeys,
    portalEl,
    refocusTriggerOnClose,
    boxShadow = '0px 8px 16px rgba(0, 0, 0, 0.16)',
    openWithArrowKeys = true,
    ignoreClasses = [],
    'data-component': dataComponent,
    stopPropagation = false,
    buttonHoverColor = 'var(--gray-500)',
    forceClosed,
    border,
    onTriggerButtonClick,
    ignoreClickAway,
    onPointerUpOutside,
    onEscape,
    trackScrollElRef,
    multiClickAway,
    trackOnWindowScroll = false,
  }: PopoverProps) => {
    // State & Timeouts
    const state = usePopoverState({ defaultOpen, propsId });
    const timeouts = usePopoverTimeouts();

    // broadcast signal whether this popover is open/closed
    // we can then use the useIsModalOpen hook to detect if any modal is open by passing in the id to that hook
    useEffect(() => {
      EventEmitter.broadcastEvent(
        `${state.id}::popover-open`,
        !!(state.open && state.display === 'block' && !state.closing)
      );
    }, [state.closing, state.display, state.id, state.open]);

    const closeOnTabAway = useMemo(() => {
      if (portalEl && _closeOnTabAway === undefined) {
        return false;
      }
      if (_closeOnTabAway === undefined) {
        return true;
      }
      return _closeOnTabAway;
    }, [_closeOnTabAway, portalEl]);

    // Focus Management
    const autoFocus = useOpenAutoFocus({
      contentRef: state.contentRef,
      timeouts,
    });

    const refocusTrigger = useRefocusTriggerOnClose({
      triggerRef: state.triggerRef,
      enabled: refocusTriggerOnClose ?? false,
    });

    // Core Interactions
    const { handleOpen, handleClose } = usePopoverInteractionHandlers({
      onOpen,
      onClose,
      previousState: state.previousState,
      setOpen: state.setOpen,
      setClosing: state.setClosing,
      setDisplay: state.setDisplay,
      setMountContent: state.setMountContent,
      setHasOpenedAtLeastOnce: state.setHasOpenedAtLeastOnce,
      timeouts,
      autoFocus,
      refocusTrigger,
    });

    // Listen for open/close/toggle requests broadcast by usePopover (mirrors useFixedDrawer pattern).
    useEffect(() => {
      const handleOpenRequest = () => handleOpen();
      const handleCloseRequest = () => handleClose();
      const handleToggleRequest = () => (state.open ? handleClose() : handleOpen());

      EventEmitter.addEventListener(`${state.id}::popover-open-request`, handleOpenRequest);
      EventEmitter.addEventListener(`${state.id}::popover-close-request`, handleCloseRequest);
      EventEmitter.addEventListener(`${state.id}::popover-toggle-request`, handleToggleRequest);

      return () => {
        EventEmitter.removeEventListener(`${state.id}::popover-open-request`, handleOpenRequest);
        EventEmitter.removeEventListener(`${state.id}::popover-close-request`, handleCloseRequest);
        EventEmitter.removeEventListener(`${state.id}::popover-toggle-request`, handleToggleRequest);
      };
    }, [handleClose, handleOpen, state.id, state.open]);

    // Dimensions & Position
    const { triggerBounds } = useTriggerDimensionTracking({
      triggerRef: state.triggerRef,
      open: state.open,
      recalcutePosition,
      trackScrollElRef,
      trackOnWindowScroll,
      triggerBoundsRecalc: state.triggerBoundsRecalc,
    });

    const calculatePosition = useCalculatePosition({
      display: state.display,
      contentContainerRef: state.contentContainerRef,
      contentRef: state.contentRef,
      triggerRef: state.triggerRef,
      setSlideFrom: state.setSlideFrom,
      open: state.open,
      portalEl,
      verticalOffset,
      horizontalOffset,
      forcePosition,
      preferredPosition,
    });

    useEffect(() => {
      if (portalEl && state.open) {
        state.setTriggerBoundsRecalc((prev) => prev + 1);
      }
      // linter keeps saying "state is missing", but we're being explicit about what we want off it
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.open, portalEl, state.setTriggerBoundsRecalc]);

    useEffect(() => {
      calculatePosition();
    }, [state.open, triggerBounds, calculatePosition]);

    // Advanced Features
    usePopoverClickAway({
      containerRef: state.containerRef,
      portalEl,
      open: state.open,
      closeOnClick,
      closeOnEscape,
      closeOnTabAway,
      ignoreClasses,
      ignoreClickAway,
      multiClickAway,
      handleClose,
      onPointerUpOutside,
      onEscape,
    });

    const { handleOpenWithArrowKeys } = usePopoverKeyboardControls({
      cycleWithArrowKeys,
      closeOnEnter,
      openWithArrowKeys,
      open: state.open,
      display: state.display,
      portalEl,
      contentRef: state.contentRef,
      handleOpen,
      handleClose,
    });

    useControlledPopover({
      controlledOpen,
      forceClosed,
      display: state.display,
      mountContent: state.mountContent,
      hasOpenedAtLeastOnce: state.hasOpenedAtLeastOnce,
      previousState: state.previousState,
      handleOpen,
      handleClose,
    });

    // Render Helpers
    const clonedTriggerButton = useTriggerButtonClone({
      renderTriggerButton,
      triggerRef: state.triggerRef,
      open: state.open,
      closing: state.closing,
      disabled,
      controlledOpen,
      keepOpenOnTriggerToggle,
      ignoreClasses,
      handleOpen,
      handleClose,
      handleOpenWithArrowKeys,
      onTriggerButtonClick,
      stopPropagation,
    });

    const animationStyles = buildAnimationStyles({
      display: state.display,
      slideFrom: state.slideFrom,
      forceTransformIn,
      forceTransformOut,
    });

    const containerStyles = buildContentContainerStyles({
      popoverWidth,
      triggerWidth: state.triggerRef.current?.offsetWidth,
      portalEl,
      open: state.open,
    });

    const ChildrenWrapper = useMemo(() => (portalEl ? Portal : Fragment), [portalEl]);

    const childrenProps = useMemo(() => {
      if (portalEl) {
        return {
          ref: portalEl,
          id: state.portalId,
          className: `popover-portal ${styles['popover-portal']} ${state.display === 'block' ? styles['popover-portal-display'] : ''}`,
        };
      }
      return {};
    }, [state.display, portalEl, state.portalId]);

    return (
      <div
        data-component={dataComponent || 'Popover'}
        ref={(r) => {
          state.containerRef.current = r;
          if (ref) {
            if (typeof ref === 'function') {
              ref(r);
            } else {
              ref.current = r;
            }
          }
        }}
        className={`${styles['popover-container']} ${containerClassName || ''}`}
        id={state.id}
      >
        {clonedTriggerButton() || (
          <Button
            disabled={disabled}
            onClick={(e) => {
              if (stopPropagation) {
                e.stopPropagation();
              }
              onTriggerButtonClick?.(e);
              if (controlledOpen !== undefined) return;
              if (state.open) {
                if (keepOpenOnTriggerToggle) return;
                handleClose();
              } else {
                handleOpen();
              }
            }}
            onKeyDown={handleOpenWithArrowKeys}
            hoverBackgroundColor={buttonHoverColor}
            ref={state.triggerRef}
            className={`flex-column jc-center ai-center square-30 radius-full padding-0 ${styles.btn}`}
            style={
              {
                ...(buttonBgColor ? { '--popover-button-bg-color': buttonBgColor } : {}),
              } as CSSProperties
            }
          >
            <span className="flex-column jc-center ai-center">
              <Icon
                name={icon || ICONS.THREE_DOTS_HORIZONTAL}
                size={16}
                color={iconColor}
                lightModeColor={iconColorLightMode || 'var(--gray-1000)'}
              />
            </span>
          </Button>
        )}
        <div
          ref={portalEl ? null : state.contentContainerRef}
          className={styles['popover-content-container']}
          style={containerStyles}
        >
          <div
            className={`${styles['popover-content']} ${contentClassName || ''} ${inheritRadius ? styles['inherit-radius'] : ''} ${state.open ? styles['open'] : ''} ${state.display === 'block' ? styles['display-block'] : ''}`}
            style={{
              width: popoverWidth || width,
              ...animationStyles,
              ...(height ? { height } : { height: 'fit-content' }),
              ...(maxHeight ? { maxHeight } : {}),
              ...(backgroundColor ? { backgroundColor } : {}),
              ...(overflow ? { '--popover-overflow': overflow } : {}),
              ...(radius ? { '--popover-radius': radius } : {}),
              ...(border ? { '--popover-border': border } : {}),
              ...(boxShadow ? { '--popover-box-shadow': boxShadow } : {}),
            }}
            ref={mergeRefs(state.contentRef, popoverContentRef)}
          >
            {state.mountContent ? (
              <ChildrenWrapper {...childrenProps}>
                {typeof children === 'function'
                  ? children({
                      triggerBounds,
                      handleClose,
                      handleOpen,
                      open: state.open,
                      closing: state.closing,
                      boxShadow,
                    })
                  : children}
              </ChildrenWrapper>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);

export default Popover;
